<template>
  <!-- The stridy.com site-nav row, sitting above the dashboard's own header
       inside the app bar (so it slides away with it on scroll). Desktop only —
       below 800px the same links live in App.vue's burger menu. -->
  <div class="site-header" :style="{ height: siteHeaderPx + 'px' }">
    <div class="site-header__inner">
      <a class="site-header__logo" :href="siteUrl" target="_top">
        <img :src="logoSrc" :srcset="logoSrcset" sizes="220px" alt="Stridy" />
      </a>

      <nav class="site-nav" aria-label="Stridy site menu">
        <ul class="site-nav__list">
          <li
            v-for="item in nav"
            :key="item.label"
            class="site-nav__item"
            :class="{ 'site-nav__item--parent': item.children }"
          >
            <!-- Parent of a submenu: a button, not a link — the live site's
                 href="#" anchor exists only so Elementor can hang a dropdown
                 off it, and it navigates nowhere. -->
            <button
              v-if="item.children"
              type="button"
              class="site-nav__link"
              aria-haspopup="true"
            >
              {{ item.label }}
              <v-icon class="site-nav__caret" size="32">arrow_drop_down</v-icon>
            </button>
            <a
              v-else
              class="site-nav__link"
              :class="{ 'site-nav__link--current': item.current }"
              :href="item.href"
              :aria-current="item.current ? 'page' : null"
              target="_top"
            >
              {{ item.label }}
            </a>

            <!-- Opened by :hover / :focus-within on the parent <li> (see the
                 SCSS) — no JS, so it works for pointer and keyboard alike. -->
            <ul v-if="item.children" class="site-nav__submenu">
              <li v-for="child in item.children" :key="child.label">
                <a :href="child.href" target="_top">{{ child.label }}</a>
              </li>
            </ul>
          </li>
        </ul>
      </nav>

      <!-- <a
        class="site-header__account"
        :href="accountUrl"
        target="_top"
        aria-label="Account"
      >
        <v-icon>account_circle</v-icon>
      </a> -->
    </div>
  </div>
</template>

<script>
import {
  SITE_ACCOUNT_URL,
  SITE_HEADER_PX,
  SITE_LOGO_SRC,
  SITE_LOGO_SRCSET,
  SITE_NAV,
  SITE_URL,
} from "@/constants/siteNav";

export default {
  name: "SiteHeader",

  data: () => ({
    nav: SITE_NAV,
    siteUrl: SITE_URL,
    accountUrl: SITE_ACCOUNT_URL,
    logoSrc: SITE_LOGO_SRC,
    logoSrcset: SITE_LOGO_SRCSET,
    siteHeaderPx: SITE_HEADER_PX,
  }),
};
</script>

<style lang="scss" scoped>
@import "@/styles/mixins.scss";
@import "@/styles/variables.scss";

// Gap between nav links; also the submenu's own horizontal padding.
$nav-gap-min: 38px;
$nav-gap-max: 68px;
$nav-gap: #{fluid($nav-gap-min, $nav-gap-max)};
$nav-gap-half: #{fluid($nav-gap-min / 2, $nav-gap-max / 2)};
$nav-gap-half-neg: #{fluid($nav-gap-min / -2, $nav-gap-max / -2)};

// Cap on the logo-to-nav spread; auto margin centers the row once it hits this.
$content-max-width: 1400px;

// Minimum breathing room at the window edges below the max-width.
$edge-pad: 40px;

// The site-nav row keeps the brand lime in both themes, like the live site — so
// everything on it uses $color-on-primary (the constant dark-green foreground
// for the lime), not the theme-flipping text colours.
.site-header {
  flex: 0 0 auto;
  width: 100%;
  background-color: var(--v-primary-base);
  color: $color-on-primary;
}

.site-header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  max-width: $content-max-width;
  margin: 0 auto;
  padding: 0 $edge-pad;
}

$logo-min: 38px;
$logo-max: 58px;

.site-header__logo {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  margin-right: 20px;

  img {
    display: block;
    height: #{fluid($logo-min, $logo-max)};
    width: auto;
  }
}

.site-nav__list {
  display: flex;
  align-items: center;
  column-gap: $nav-gap;
  list-style: none;
  padding: 0;
  margin: 0;
}

.site-nav__item {
  position: relative;
  display: flex;
  align-items: center;

  // The site separates its menu items with a slash. Drawn in the gap between
  // items (not as markup) so it stays out of the tab order and the screen
  // reader's path through the menu.
  & + &::before {
    content: "/";
    position: absolute;
    left: #{$nav-gap-half-neg};
    transform: translateX(-50%);
    font-size: 28px;
    font-weight: 800;
    line-height: 1;
    pointer-events: none;
  }
}

.site-nav__link {
  display: inline-flex;
  align-items: center;
  position: relative;
  padding: 6px 0;
  border: 0;
  background: none;
  font-size: 21px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  color: $color-on-primary;
  text-decoration: none;
  cursor: pointer;

  // The site's underline pointer: a bar that fades in under the label on hover
  // and stays put for the current page.
  &::after {
    content: "";
    position: absolute;
    left: #{$nav-gap-half-neg};
    right: #{$nav-gap-half-neg};
    bottom: #{$nav-gap-half-neg};
    height: 3px;
    background-color: $color-on-primary;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover::after,
  &:focus-visible::after,
  &--current::after {
    opacity: 1;
  }
}

.site-nav__caret {
  margin-left: 2px;
  color: inherit !important;
}

.site-nav__submenu {
  position: absolute;
  top: 100%;
  left: #{$nav-gap-half-neg};
  z-index: 10;
  min-width: 190px;
  padding: 8px 0;
  margin: 0;
  list-style: none;
  background-color: $color-raised;
  border: 1px solid $color-border;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.14);
  // Hidden but focusable-in via focus-within, hence visibility (not display)
  // so the fade can run.
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;

  a {
    display: block;
    padding: 8px #{$nav-gap-half};
    font-size: 14px;
    white-space: nowrap;
    color: $color-text-primary;
    text-decoration: none;

    &:hover,
    &:focus-visible {
      background-color: $color-panel;
    }
  }
}

.theme--dark .site-nav__submenu {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.5);
}

.site-nav__item--parent:hover .site-nav__submenu,
.site-nav__item--parent:focus-within .site-nav__submenu {
  opacity: 1;
  visibility: visible;
}

.site-header__account {
  display: flex;
  align-items: center;
  margin-left: $nav-gap;
  text-decoration: none;

  .v-icon {
    color: $color-on-primary;
    transition: opacity 0.2s ease;
  }

  &:hover .v-icon,
  &:focus-visible .v-icon {
    opacity: 0.65;
  }
}
</style>
